import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import authService from '../services/auth';

/**
 * AuthRedirect Component
 *
 * Handles secure auth token management:
 * 1. If auth token is in URL, extracts and stores it
 * 2. Redirects to clean URL (without auth token)
 * 3. If no auth in URL but token in storage, renders children
 * 4. If no auth anywhere, shows error
 *
 * Usage: Wrap this around components that need authentication
 * <AuthRedirect><YourComponent /></AuthRedirect>
 */
const AuthRedirect = ({ children }) => {
  const { auth, uuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);
  const [hasAuth, setHasAuth] = useState(false);

  useEffect(() => {
    // Check if auth token is in URL
    if (auth) {
      // Store the auth token securely
      authService.extractAndStore(auth);

      // Determine the clean URL path (without auth)
      const pathParts = location.pathname.split('/');
      // Path format: /route/:auth/:uuid -> /route/:uuid
      // Find the route type (booking, approved, declined, cancelled)
      const routeType = pathParts[1];

      // Redirect to clean URL
      navigate(`/${routeType}/${uuid}`, { replace: true });
      setHasAuth(true);
      setIsReady(true);
    } else if (authService.hasToken()) {
      // No auth in URL but token exists in storage
      setHasAuth(true);
      setIsReady(true);
    } else {
      // No auth anywhere - this is an error state
      setHasAuth(false);
      setIsReady(true);
    }
  }, [auth, uuid, navigate, location.pathname]);

  // Show loading while processing
  if (!isReady) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <div className="text-center">
          <svg
            className="animate-spin h-10 w-10 text-blue-600 mx-auto"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error if no auth
  if (!hasAuth) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 text-red-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Authentication Required
          </h2>
          <p className="text-gray-600 mb-6">
            This booking link has expired or is invalid. Please use the original
            link from your email to access your booking.
          </p>
          <div className="mt-6 pt-4 border-t text-sm text-gray-400">
            <span>0800 36 77 35 | info@rubbishdirect.co.nz</span>
          </div>
        </div>
      </div>
    );
  }

  // Auth is valid, render children
  return children;
};

export default AuthRedirect;
